import { Logger } from '../../utils/logger';
import { getDataFromPocket } from '../../utils/pocket';

// ==== Model
const STORAGE_KEY = 'acronis_ab_storage';

interface SplitVariant {
    id: string;
    size: number;
    isControl: boolean;
}

interface SplitURLRestriction {
    reg: string;
    flags: string;
}

interface Split {
    id: string;
    dimension: string;
    variants: SplitVariant[];
    urlsAllowed: SplitURLRestriction[];
}

type Storage = Record<string, string>;

// ==== Helpers
function isControlMode(): boolean {
    return !window.localStorage || !window.localStorage.getItem;
}

function hasStrongerRandSupport(): boolean {
    return Boolean(window.crypto?.getRandomValues);
}

function diceRollClassic(): number {
    return Math.floor(Math.random() * 100);
}

function diceRollModern(): number {
    const bytes = new Uint32Array(1);
    crypto.getRandomValues(bytes);

    let val = (bytes[0] as number) / (0xffffffff + 1);
    val = Math.floor(val * 100);

    return val;
}

const diceRoll = hasStrongerRandSupport() ? diceRollModern : diceRollClassic;

// ==== Storage
function unpackStorage(): Storage {
    try {
        const data = window.localStorage.getItem(STORAGE_KEY) || '{}';
        return JSON.parse(data) as Storage;
    } catch (e) {
        return {};
    }
}

const storage: Storage = unpackStorage();

function packStorage() {
    const json = JSON.stringify(storage, null, 2);
    window.localStorage.setItem(STORAGE_KEY, json);
}

// ==== Splits logic
function hasStoredVariant(id: string): boolean {
    return id in storage;
}

function getStoredVariant(id: string): string | undefined {
    return storage[id];
}

function storeVariant(id: string, val: string) {
    storage[id] = val;
}

function getCalculatedVariant(variants: SplitVariant[]): string | null {
    const token = diceRoll();

    let treshold = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const variant of variants) {
        treshold += variant.size;

        if (token < treshold) {
            return variant.id;
        }
    }

    return null;
}

function isSplitValid(split: Split): boolean {
    const isValid = split.id &&
        split.variants.find((x) => x.isControl) &&
        split.variants.reduce((acc, next) => acc + next.size, 0) === 100;

    return Boolean(isValid);
}

function isSplitAllowedOnURL(split: Split) {
    if (!split.urlsAllowed || !split.urlsAllowed.length) {
        return true;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const target of split.urlsAllowed) {
        try {
            const r = new RegExp(target.reg, target.flags || 'i');
            if (r.test(window.location.toString())) {
                return true;
            }
        } catch (e) {
            Logger.error('[AB] Failed to construct urlsAllowed regexp', target);
        }
    }

    return false;
}

// ==== Public interface
export function initSplits() {
    const synced = getDataFromPocket<{ items: Split[] }>('splits')!;

    const master = synced.items.filter(isSplitValid);

    const active = master.map((split) => {
        const id = split.id;

        if (isControlMode() || !isSplitAllowedOnURL(split)) {
            const variant = split.variants.find((x) => x.isControl) as SplitVariant;

            return {
                id,
                variant: variant.id,
                dimension: null,
            };
        }

        if (hasStoredVariant(id)) {
            return {
                id,
                variant: getStoredVariant(id)!,
                dimension: split.dimension,
            };
        }

        const res = {
            id,
            variant: getCalculatedVariant(split.variants)!,
            dimension: split.dimension,
        };

        storeVariant(id, res.variant);
        packStorage();

        return res;
    });

    const domRoot = document.querySelector('html')!;
    active.forEach((x) => {
        if (x.dimension) {
            domRoot.classList.add(`split-${x.id}-${x.variant}`);
        }
    });

    // As this script is in "critical JS path", we have to share the state globally
    window.splitsCurrentActiveSet = active;

    if (isControlMode()) {
        Logger.info('[AB] Control mode');
        return;
    }

    // Removing obsolete records
    const currentActiveSet = new Set(synced.items.map((x) => x.id));

    Object.keys(storage)
        .filter((id) => !currentActiveSet.has(id))
        .forEach((id) => {
            delete storage[id];
        });

    packStorage();
}
